import React, { useEffect } from 'react';
import { Route, Routes, Navigate, useParams } from 'react-router-dom';
import { CONFIG } from 'utils/config';
import { FilterName, RoutePaths } from 'types/enums';
import Sfxs from 'pages/Sfxs/Sfxs';
import { ChartPage } from 'pages/ChartPage/ChartPage';
import { lazyWithRetries } from 'utils/lazyWithRetries';
import PageUnknownError from 'error/pages/PageUnknownError/PageUnknownError';
import { useAuthentication } from 'contexts/AuthenticationProvider/AuthenticationProvider';
import { useExecutionEnvironmentContext } from 'contexts/ExecutionEnvironmentContext/ExecutionEnvironmentContext';
import { useLocalePrefixes } from 'hooks/useLocalePrefixes/useLocalePrefixes';
import { ProtectedRoute } from './ProtectedRoute';

// PAGES
const ForYou = lazyWithRetries(() => import('pages/ForYou/ForYou'));
const Browse = lazyWithRetries(() => import('pages/Browse/Browse'));
const Favorites = lazyWithRetries(() => import('pages/Favorites/Favorites'));
const PackDetails = lazyWithRetries(
    () => import('pages/PackDetails/PackDetails'),
);
const RePackDetails = lazyWithRetries(
    () => import('pages/RePackDetails/RePackDetails'),
);
const Exclusives = lazyWithRetries(() => import('pages/Exclusives/Exclusives'));
const Labels = lazyWithRetries(() => import('pages/Labels/Labels'));
const ArtistDetails = lazyWithRetries(
    () => import('pages/ArtistDetails/ArtistDetails'),
);
const Instruments = lazyWithRetries(
    () => import('pages/Instruments/Instruments'),
);
const Genres = lazyWithRetries(() => import('pages/Genres/Genres'));
const Recommendation = lazyWithRetries(
    () => import('pages/Recommendation/Recommendation'),
);
const LabelDetails = lazyWithRetries(
    () => import('pages/LabelDetails/LabelDetails'),
);
const Downloads = lazyWithRetries(() => import('pages/Downloads/Downloads'));
const CreatorLandingPage = lazyWithRetries(
    () => import('pages/CreatorLandingPage/CreatorLandingPage'),
);
const UserCollections = lazyWithRetries(
    () => import('pages/UserCollections/UserCollections'),
);
const UserCollectionDetails = lazyWithRetries(
    () => import('pages/UserCollectionDetails/UserCollectionDetails'),
);
const QuickPicks = lazyWithRetries(() => import('pages/QuickPicks/QuickPicks'));
const Collections = lazyWithRetries(
    () => import('pages/Collections/Collections'),
);
const BrandtypeDetails = lazyWithRetries(
    () => import('pages/BrandtypeDetails/BrandtypeDetails'),
);
const KeyBpmFinder = lazyWithRetries(() => import('pages/KeyBpmFinder'));

const NavigateTo = ({ url }: { url: string }) => {
    useEffect(() => {
        window.location.href = url;
    }, []);

    return null;
};

const CreatorHashRedirect = ({ locale }: { locale: string }) => {
    const { hash } = useParams<{ hash: string }>();

    return (
        <Navigate
            to={`${locale}${RoutePaths.CreatorBeatMaker}/${hash}`}
            replace
        />
    );
};

const useAppRoutes = () => {
    const { isAuthenticated, isLoading } = useAuthentication();

    const { isDAWPlugin, isDesktopApp } = useExecutionEnvironmentContext();

    const isPrivateRoutesAllowed = isAuthenticated;
    const isPublicRoutesAllowed =
        (!isDAWPlugin && !isDesktopApp) || isAuthenticated;

    return {
        isLoading,
        isPrivateRoutesAllowed,
        isPublicRoutesAllowed,
    };
};

export const AppRoutes = () => {
    const { isLoading, isPrivateRoutesAllowed, isPublicRoutesAllowed } =
        useAppRoutes();
    const localePrefixes = useLocalePrefixes();

    return (
        <Routes>
            {localePrefixes.map((locale) => (
                <Route
                    key="protected-route-isPublicRoutesAllowed"
                    element={
                        <ProtectedRoute
                            isLoading={isLoading}
                            isAllowed={isPublicRoutesAllowed}
                        />
                    }
                >
                    <Route key="for-you" path={locale} element={<ForYou />} />
                    <Route
                        key="legacy-creator"
                        path={`${locale}${RoutePaths.Creator}`}
                        element={
                            <Navigate
                                to={`${locale}${RoutePaths.CreatorBeatMaker}`}
                                replace
                            />
                        }
                    />
                    <Route
                        key="legacy-creator-hash"
                        path={`${locale}${RoutePaths.Creator}/:hash`}
                        element={<CreatorHashRedirect locale={locale} />}
                    />
                    <Route
                        key="creator-beat-maker"
                        path={`${locale}${RoutePaths.CreatorBeatMaker}`}
                        element={<CreatorLandingPage />}
                    />

                    <Route
                        key="creator-beat-maker-hash"
                        path={`${locale}${RoutePaths.CreatorBeatMaker}/:hash`}
                        element={<CreatorLandingPage />}
                    />
                    <Route
                        key="plugin"
                        path={`${locale}${RoutePaths.Plugin}`}
                        element={
                            <NavigateTo url={CONFIG.VITE_SAMPLES_PLUGIN_URL} />
                        }
                    />
                    <Route
                        key="key-bpm-finder"
                        path={`${locale}${RoutePaths.KeyBpmFinder}`}
                        element={<KeyBpmFinder />}
                    />
                    <Route
                        key="instruments"
                        path={`${locale}${RoutePaths.Instruments}`}
                        element={<Instruments />}
                    />
                    <Route
                        key="descriptors"
                        path={`${locale}${RoutePaths.Descriptors}/:descriptors`}
                        element={
                            <Navigate
                                to={`${locale}${RoutePaths.Browse}`}
                                replace
                            />
                        }
                    />
                    <Route
                        key="genres"
                        path={`${locale}${RoutePaths.Genres}`}
                        element={<Genres />}
                    />
                    <Route
                        key="sfxs"
                        path={`${locale}${RoutePaths.Sfxs}`}
                        element={<Sfxs />}
                    />
                    <Route
                        key="pricing"
                        path={`${locale}${RoutePaths.Pricing}`}
                        element={
                            <NavigateTo
                                url={`${
                                    CONFIG.VITE_PRICING_URL
                                }/samples?returnUrl=${encodeURIComponent(
                                    CONFIG.VITE_SAMPLES_WEB_URL,
                                )}`}
                            />
                        }
                    />
                    <Route
                        key="recommendations"
                        path={`${locale}${RoutePaths.Recommendations}/:id`}
                        element={<Recommendation />}
                    />

                    <Route
                        key="quick-picks"
                        path={`${locale}${RoutePaths.QuickPicks}`}
                        element={<QuickPicks />}
                    />
                    <Route
                        key="collections"
                        path={`${locale}${RoutePaths.Collections}`}
                        element={<Collections />}
                    />
                    <Route
                        key="charts"
                        path={`${locale}${RoutePaths.Charts}`}
                        element={
                            <Navigate
                                to={`${locale}${RoutePaths.Exclusives}`}
                                replace
                            />
                        }
                    />
                    <Route
                        key="charts-slug"
                        path={`${locale}${RoutePaths.Charts}/:slug`}
                        element={<ChartPage />}
                    />
                    <Route
                        key="exclusives"
                        path={`${locale}${RoutePaths.Exclusives}`}
                        element={<Exclusives />}
                    />
                    <Route
                        key="artists"
                        path={`${locale}${RoutePaths.Artists}`}
                        element={
                            <Navigate
                                to={`${locale}${RoutePaths.Exclusives}`}
                                replace
                            />
                        }
                    />
                    <Route
                        key="labels"
                        path={`${locale}${RoutePaths.Labels}`}
                        element={<Labels />}
                    />

                    <Route
                        key="category-slug"
                        path={`${locale}${RoutePaths.Category}/:slug`}
                        element={<BrandtypeDetails />}
                    />
                    <Route
                        key="artists-slug"
                        path={`${locale}${RoutePaths.Artists}/:slug`}
                        element={<ArtistDetails />}
                    />
                    <Route
                        key="labels-slug"
                        path={`${locale}${RoutePaths.Labels}/:slug`}
                        element={<LabelDetails />}
                    />
                    <Route
                        key="browse"
                        path={`${locale}${RoutePaths.Browse}`}
                        element={<Browse />}
                    />
                    <Route
                        key="instruments-slug"
                        path={`${locale}${RoutePaths.Instruments}/:${FilterName.Instruments}`}
                        element={<Browse />}
                    />
                    <Route
                        key="genres-slug"
                        path={`${locale}${RoutePaths.Genres}/:${FilterName.Genres}`}
                        element={<Browse />}
                    />
                    <Route
                        key="sfxs-slug"
                        path={`${locale}${RoutePaths.Sfxs}/:${FilterName.Sfxs}`}
                        element={<Browse />}
                    />
                    <Route
                        key="packs-slug"
                        path={`${locale}${RoutePaths.Packs}/:slug`}
                        element={<PackDetails />}
                    />
                    <Route
                        key="repacks-slug"
                        path={`${locale}${RoutePaths.Collections}/:slug`}
                        element={<RePackDetails />}
                    />
                    <Route
                        key="protected-route-isPrivateRoutesAllowed"
                        element={
                            <ProtectedRoute
                                isLoading={isLoading}
                                isAllowed={isPrivateRoutesAllowed}
                            />
                        }
                    >
                        <Route
                            key="downloads"
                            path={`${locale}${RoutePaths.Downloads}`}
                            element={<Downloads />}
                        />
                        <Route
                            key="favorites"
                            path={`${locale}${RoutePaths.Favorites}`}
                            element={<Favorites />}
                        />
                        <Route
                            key="user-collections"
                            path={`${locale}${RoutePaths.UserCollections}`}
                            element={<UserCollections />}
                        />
                        <Route
                            key="user-collections-guid"
                            path={`${locale}${RoutePaths.UserCollections}/:guid`}
                            element={<UserCollectionDetails />}
                        />
                    </Route>
                    <Route
                        key="page-unknown-error"
                        path="*"
                        element={<PageUnknownError />}
                    />
                </Route>
            ))}
        </Routes>
    );
};
